import React, { useEffect, useState } from 'react';
import { apiGet } from '../utils/backendApi';

// Tiny inline sparklines over the fleet vitals rings
// (backend/shared/vitalsStore.js → /apps/admin/vitals, /apps/:orgId/vitals).
// Pure SVG, no chart lib: one polyline + an optional cap line + markers for
// restart / OOM minutes. `points` = [{ ts, v }], `max` scales the y axis
// (defaults to the series max), `marks` = [{ ts, kind: 'restart'|'oom' }].
export function Spark({ points, max, warnAt, marks = [], width = 120, height = 24, title, unit = '' }) {
  const pts = (points || []).filter((p) => Number.isFinite(p.v));
  if (pts.length < 2) return <span className="cs-help" title={title}>{pts.length ? `${Math.round(pts[0].v)}${unit}` : '—'}</span>;
  const t0 = pts[0].ts; const t1 = pts[pts.length - 1].ts || t0 + 1;
  const top = Math.max(max || 0, ...pts.map((p) => p.v)) || 1;
  const x = (ts) => ((ts - t0) / Math.max(1, t1 - t0)) * (width - 2) + 1;
  const y = (v) => height - 1 - (Math.min(v, top) / top) * (height - 2);
  const d = pts.map((p) => `${x(p.ts).toFixed(1)},${y(p.v).toFixed(1)}`).join(' ');
  const last = pts[pts.length - 1].v;
  const hot = warnAt != null && last >= warnAt;
  return (
    <svg className={`vs-spark${hot ? ' is-hot' : ''}`} width={width} height={height} viewBox={`0 0 ${width} ${height}`} role="img" aria-label={title || 'trend'}>
      <title>{title || `${Math.round(last)}${unit} now · peak ${Math.round(top)}${unit}`}</title>
      {warnAt != null && warnAt < top ? <line x1={0} x2={width} y1={y(warnAt)} y2={y(warnAt)} className="vs-spark-warn" /> : null}
      <polyline points={d} fill="none" className="vs-spark-line" />
      {marks.map((m, i) => (
        <circle key={i} cx={x(m.ts)} cy={height - 3} r={2} className={`vs-spark-mark is-${m.kind}`}><title>{`${m.kind} · ${new Date(m.ts).toLocaleTimeString()}`}</title></circle>
      ))}
    </svg>
  );
}

// One fetch of the vitals rings for a page; re-pulled every 60 s while visible.
export function useVitals(path, { window = '24h' } = {}) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!path) return undefined;
    let alive = true;
    const pull = () => {
      if (document.visibilityState !== 'visible') return;
      apiGet(`${path}${path.includes('?') ? '&' : '?'}window=${window}`).then((d) => { if (alive) setData(d); }).catch(() => {});
    };
    pull();
    const t = setInterval(pull, 60_000);
    return () => { alive = false; clearInterval(t); };
  }, [path, window]);
  return data;
}

// Per-service memory series out of an instance ring (compact keys: svc.m
// = avg MB, x = max MB, l = cap MB, r = restart count, o = oom, d = died).
export function serviceMemory(rows, svc) {
  const out = []; const marks = []; let cap = 0; let prevR = null;
  for (const r of rows || []) {
    const s = r.svc?.[svc];
    if (!s) continue;
    out.push({ ts: r.ts, v: s.m });
    if (s.l) cap = s.l;
    if (s.o > 0) marks.push({ ts: r.ts, kind: 'oom' });
    else if (s.d > 0 || (prevR != null && s.r != null && s.r !== prevR)) marks.push({ ts: r.ts, kind: 'restart' });
    if (s.r != null) prevR = s.r;
  }
  return { points: out, marks, cap };
}

export const hostMemory = (rows) => (rows || []).map((r) => ({ ts: r.ts, v: r.mem }));
